/**
 * ProgressBar Component
 * Dynamic indicator driven by useStepNavigation (entity-aware steps)
 * Desktop: full step bar with labels
 * Mobile: scrollable dots + current step text
 */

import React, { useRef, useEffect } from 'react';
import { Check } from 'lucide-react';
import { useStepNavigation } from '@/hooks/useStepNavigation';
import { cn } from '@/utils/helpers';

const ProgressBar: React.FC = () => {
  const { steps, currentStepIndex } = useStepNavigation();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll current step dot into view on mobile
  useEffect(() => {
    if (scrollRef.current) {
      const dots = scrollRef.current.children;
      const current = dots[currentStepIndex] as HTMLElement | undefined;
      current?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [currentStepIndex]);

  // Exclude the "success" step from the progress display
  const displaySteps = steps.filter((s) => s.id !== 'success');
  const displayIndex = Math.min(currentStepIndex, displaySteps.length - 1);
  const progressPercent = displaySteps.length > 1 ? (displayIndex / (displaySteps.length - 1)) * 100 : 0;

  return (
    <div className="bg-white border-b border-neutral-200 py-4 sm:py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Desktop: full step bar (hidden below md) */}
        <div className="hidden md:block relative">
          {/* Progress Line */}
          <div className="absolute top-5 left-0 w-full h-1 bg-neutral-200">
            <div
              className="h-full bg-primary-600 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Steps */}
          <div className="relative flex items-center justify-between">
            {displaySteps.map((step, idx) => {
              const isCompleted = idx < currentStepIndex;
              const isCurrent = idx === currentStepIndex;
              const isUpcoming = idx > currentStepIndex;

              return (
                <div
                  key={step.id}
                  className="flex flex-col items-center"
                  style={{ width: `${100 / displaySteps.length}%` }}
                >
                  <div
                    className={cn(
                      'relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300',
                      {
                        'bg-primary-600 border-primary-600 text-white': isCompleted || isCurrent,
                        'bg-white border-neutral-300 text-neutral-400': isUpcoming,
                        'ring-4 ring-primary-100': isCurrent,
                      },
                    )}
                  >
                    {isCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-semibold">{idx + 1}</span>
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <p
                      className={cn('text-xs font-medium truncate max-w-[80px]', {
                        'text-primary-600': isCompleted || isCurrent,
                        'text-neutral-400': isUpcoming,
                      })}
                    >
                      {step.shortLabel}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: progress bar + label (visible below md) */}
        <div className="md:hidden space-y-2">
          {/* Progress percentage bar */}
          <div className="relative h-2 bg-neutral-200 rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-primary-600 rounded-full transition-all duration-500"
              style={{ width: `${Math.round(progressPercent)}%` }}
            />
          </div>

          {/* Current step info with percentage */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-600">
              Step{' '}
              <span className="font-semibold text-primary-600">{currentStepIndex + 1}</span>{' '}
              of <span className="font-semibold">{displaySteps.length}</span>
              {' — '}
              <span className="font-medium text-neutral-800">
                {displaySteps[displayIndex]?.label}
              </span>
            </p>
            <span className="text-xs font-semibold text-primary-600">
              {Math.round(progressPercent)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;
